<template lang="pug">
  .b-customer-spec-row(data-testid="customer-spec-row" :class="{ tile }")
    ._cell._check(:style="s('check')")
      input._check-input(
        v-if="canDecide"
        type="checkbox"
        data-testid="customer-spec-row-checkbox"
        :checked="selected"
        @change="$emit('select', $event.target.checked)"
        )
    ._cell._photo(:style="s('photo')")
      span._photo-box(data-testid="customer-spec-row-photo")
        img._photo-img(v-if="photoUrl" :src="photoUrl" :alt="item.name")

    ._cell._name(:style="s('name')")
      span._label(v-if="tile") Наименование
      span._value._strong(data-testid="customer-spec-row-name") {{ item.name }}

    ._cell(:style="s('brand')")
      span._label(v-if="tile") Бренд
      span._value(data-testid="customer-spec-row-brand") {{ item.brand || '—' }}

    ._cell(:style="s('article')")
      span._label(v-if="tile") Артикул
      span._value(data-testid="customer-spec-row-article") {{ item.article || '—' }}

    ._cell(:style="s('room')")
      span._label(v-if="tile") Помещение
      span._value(data-testid="customer-spec-row-room") {{ item.room || '—' }}

    ._cell(:style="s('quantity')")
      span._label(v-if="tile") Количество
      span._value
        span(data-testid="customer-spec-row-quantity") {{ quantity(item.quantity) }}
        = ' '
        span {{ unitTitle(item.unit) }}

    ._cell(:style="s('status')")
      span._label(v-if="tile") Статус
      span._value._status(data-testid="customer-spec-row-status") {{ statusText }}

    ._cell(:style="s('supplierPrice')")
      span._label(v-if="tile") Цена без скидки
      span._value(data-testid="customer-spec-row-supplier-price") {{ money(item.supplierPrice) }}

    ._cell(:style="s('discount')")
      span._label(v-if="tile") Скидка, ₽
      span._value(data-testid="customer-spec-row-discount") {{ money(item.customerDiscount) }}

    ._cell(:style="s('discountPercent')")
      span._label(v-if="tile") Скидка, %
      span._value(data-testid="customer-spec-row-discount-percent") {{ percent(item.customerDiscountPercent) }}

    ._cell(:style="s('customerPrice')")
      span._label(v-if="tile") Цена
      span._value._strong(data-testid="customer-spec-row-customer-price") {{ money(item.customerPrice) }}

    ._cell(:style="s('customerTotal')")
      span._label(v-if="tile") Сумма
      span._value._strong(data-testid="customer-spec-row-customer-total") {{ money(item.customerTotal) }}

    ._cell(:style="s('delivery')")
      span._label(v-if="tile") Срок поставки
      span._value(data-testid="customer-spec-row-delivery") {{ deliveryText(item.deliveryTerm, '—') }}

    ._cell(:style="s('supplier')")
      span._label(v-if="tile") Поставщик
      span._value(data-testid="customer-spec-row-supplier") {{ item.supplierName || '—' }}

    //- В таблице кнопки узкие и одна под другой — так столбец действий помещается на мониторе
    //- 1366 px вместе с «Поставщиком» (ТЗ 6.6); в плитке они обычного размера.
    ._cell._actions(:style="s('actions')")
      template(v-if="canDecide")
        Btn(
          green
          :sm="tile"
          :xs="!tile"
          :narrow="!tile"
          :stretch="!tile"
          data-testid="customer-spec-row-approve"
          @click="$emit('approve')"
          ) Утверждаю
        Btn(
          grey
          :sm="tile"
          :xs="!tile"
          :narrow="!tile"
          :stretch="!tile"
          data-testid="customer-spec-row-reject"
          @click="$emit('reject')"
          ) Отклонить
</template>

<script>
import {
  money, percent, quantity, unitTitle, deliveryText, customerItemStatusTitle,
} from '@/utils/spec';
import { colStyle } from '@/components/specification/customer/columns';

/**
 * Позиция на странице заказчика (ТЗ 6.6): строка таблицы на широком экране и плитка на ширине до
 * 768 px — один и тот же компонент, вид переключает свойство `tile`. Так набор полей и тестовых
 * меток в обоих видах заведомо одинаков, а различается только раскладка (ТЗ 6.6, 12.8).
 *
 * Все финансовые поля приходят посчитанными сервером (ТЗ 4.5): у позиции без выбранного отклика их
 * в ответе нет, и ячейки показывают тире (ТЗ 7.1, 3.5). Кнопки «Утверждаю» и «Отклонить» и чекбокс
 * существуют только в статусе «На согласовании» и только когда спецификация не «только чтение»
 * (ТЗ 6.6, 6.9). Сгенерировано Claude.
 */
export default {
  name: 'CustomerSpecRow',
  props: {
    item: { type: Object, required: true },
    /** Вид плитки: тот же набор полей, другая раскладка и подписи слева (ТЗ 6.6). */
    tile: Boolean,
    selected: Boolean,
    readOnly: Boolean,
  },
  computed: {
    canDecide() { return !this.readOnly && this.item.status === 'customer_review'; },
    statusText() { return customerItemStatusTitle(this.item.status); },
    photoUrl() {
      const { photo } = this.item;
      return photo?.thumbnails?.small ?? photo?.url ?? null;
    },
  },
  methods: {
    money,
    percent,
    quantity,
    unitTitle,
    deliveryText,
    /** В виде плитки ширины столбцов не нужны: раскладку задаёт сама плитка. */
    s(key) { return this.tile ? null : colStyle(key); },
  },
};
</script>

<style scoped lang="scss">
.customer-spec-row {
  display: flex;
  align-items: center;
  border-bottom: 1px solid var(--color-super-light);
  font-size: 12px;

  &:last-child {
    border-bottom: none;
  }

  &__cell {
    box-sizing: border-box;
    padding: 8px 6px;
    overflow: hidden;
  }

  &__value {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__strong {
    font-weight: 700;
  }

  &__label {
    display: none;
  }

  // статус переносится по словам, а не обрезается: «Согласовано поставщиком» должно читаться (6.6)
  &__status {
    white-space: normal;
    line-height: 1.2;
    color: var(--color-middle-gray);
  }

  &__photo-box {
    display: block;
    width: 30px;
    height: 30px;
    border-radius: 6px;
    background: #F1F5FA;
    overflow: hidden;
  }

  &__photo-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  &__check-input {
    width: 16px;
    height: 16px;
    cursor: pointer;
  }

  &__actions {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 4px;
  }

  // ==== вид плитки: ширина до 768 px (ТЗ 6.6) ====
  &.tile {
    display: block;
    font-size: 13px;
    border: 1px solid var(--color-super-light);
    border-radius: 8px;
    background: var(--color-white);
    padding: 12px 14px;
    position: relative;

    .customer-spec-row__cell {
      display: flex;
      align-items: baseline;
      justify-content: space-between;
      gap: 10px;
      padding: 3px 0;
      width: auto !important;
      min-width: 0 !important;
      max-width: none !important;
    }

    .customer-spec-row__label {
      display: block;
      flex: 0 0 auto;
      font-size: 11px;
      color: var(--color-middle-gray);
    }

    .customer-spec-row__value {
      white-space: normal;
      text-align: right;
    }

    .customer-spec-row__name .customer-spec-row__value {
      font-size: 15px;
    }

    .customer-spec-row__check {
      position: absolute;
      top: 12px;
      right: 14px;
      padding: 0;
    }

    .customer-spec-row__actions {
      display: flex;
      flex-direction: row;
      justify-content: flex-start;
      gap: 8px;
      padding-top: 8px;
    }

    .customer-spec-row__photo-box {
      width: 40px;
      height: 40px;
    }
  }
}
</style>
